import React, { useState, useEffect } from 'react';
import { usePoll } from '../../hooks/usePoll';
import {
  Avatar,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Box,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { api as axios } from '../../api';
import url from '../../serverUrl';

import Transactions from '../Transactions/transaction.table';
import ProChart from '../../components/ProChart/ProChart';
import SwapDiv from '../../components/SwapDiv';

import { logoWrapper } from '../../utils';
import TreasuryBackingPublic from '../../components/TreasuryBackingPublic';

// Code dictionaries for synthetic tokens
const UNDERLYING_ASSET_CODES = [
  { code: '180101', label: 'BTC' },
  { code: '180102', label: 'ETH' },
  { code: '180103', label: 'SOL' },
  { code: '180104', label: 'AVAX' },
  { code: '180105', label: 'ADA' },
  { code: '180106', label: 'DOGE' },
  { code: '180107', label: 'BNB' },
  { code: '180108', label: 'LTC' },
  { code: '180109', label: 'ETC' },
  { code: '180110', label: 'XRP' }
];

const PRICING_METHOD_CODES = [
  { code: '182101', label: 'Spot-Mirrored' },
  { code: '182102', label: 'Spot + NAV' },
  { code: '182103', label: 'Volatility Adjusted' },
  { code: '182104', label: 'Liquidity Weighted' },
  { code: '182105', label: 'Smart-Contract Oracle' },
  { code: '182106', label: 'Multi-Oracle Median' },
  { code: '182107', label: 'Forward Curve' },
  { code: '182108', label: 'Basket Weighted' }
];

const COLLATERAL_ARCHITECTURE_CODES = [
  { code: '181101', label: 'Single Asset' },
  { code: '181102', label: 'Multi Asset' },
  { code: '181103', label: 'Stablecoin Backed' },
  { code: '181104', label: 'Digital Only' },
  { code: '181105', label: 'Hybrid RWA + Digital' },
  { code: '181106', label: 'Smart-Contract Managed' },
  { code: '181107', label: 'Issuer Managed' }
];

const COLLATERALIZATION_LEVEL_CODES = [
  { code: '183101', label: 'Fully Collateralized' },
  { code: '183102', label: 'Over-Collateralized' },
  { code: '183103', label: 'Partially Collateralized' },
  { code: '183104', label: 'Dynamic' },
  { code: '183105', label: 'Volatility Responsive' }
];

const TREASURY_COMPOSITION_CODES = [
  { code: '187101', label: 'BTC Heavy' },
  { code: '187102', label: 'ETH Heavy' },
  { code: '187103', label: 'SOL Heavy' },
  { code: '187104', label: 'PECU Heavy' },
  { code: '187105', label: 'USXM Heavy' },
  { code: '187106', label: 'USDT Heavy' },
  { code: '187107', label: 'Balanced' },
  { code: '187108', label: 'Volatility Hedged' },
  { code: '187109', label: 'Yield Optimized' }
];

const formatPrice = (value) => {
  return value > 1 ? value?.toFixed(5) : value?.toFixed(5);
};

const DetailRow = ({ label, value, color = '#f0f0f0' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
      gap: 2
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', minWidth: '40%' }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color, textAlign: 'right', flex: 1, wordBreak: 'break-word' }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const DetailBlock = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#f0f0f0', wordBreak: 'break-word', lineHeight: 1.6 }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const resolveLabel = (code, list) => {
  if (!code || !Array.isArray(list)) return 'N/A';
  return list.find((i) => i.code === code)?.label || code;
};

const SyntheticPage = () => {
  const { identitySymbol } = useParams();
  const [token, setToken] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch token data
  const fetchTokenData = async () => {
    if (!identitySymbol) return;
    try {
      const res = await axios.get(
        `/crypto/get_synthetics?symbol=${identitySymbol}`
      );
      const { status, stock } = res.data || {};
      if (status && Array.isArray(stock)) {
        setToken(stock[0] || {});
      }
    } catch (err) {
      console.error('Error fetching synthetic token:', err);
    }
  };

  useEffect(() => {
    if (!identitySymbol) return;
    setLoading(true);
    fetchTokenData().finally(() => setLoading(false));
  }, [identitySymbol]);

  usePoll(() => fetchTokenData(), 5000, [identitySymbol]);

  const { symbol, currentPrice, price1hour, description, logo, name, link } =
    token || {};

  const priceChange = currentPrice - price1hour;
  const priceChangePercent = price1hour
    ? ((Math.abs(priceChange) / price1hour) * 100).toFixed(5)
    : '0.00';
  const isPositive = priceChange > 0;

  if (loading) {
    return (
      <Box sx={{ padding: { xs: 1, md: '1rem 4rem' } }}>
        <LinearProgress sx={{ backgroundColor: 'grey' }} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${identitySymbol} ${currentPrice?.toFixed(5) ?? ''}`}</title>
        <link
          rel="icon"
          href={`${url}/logo/tokenlogo/${identitySymbol}`}
          type="image"
        />
      </Helmet>

      <Box sx={{ p: { xs: 2, md: 4 }, ml: { xs: 4 } }}>
        <Grid container spacing={3} sx={{ ml: '-55px' }}>
          <SwapDiv token={identitySymbol} />

          {/* Left Column - Token Info */}
          <Grid item xs={12} md={5} mt={4} sx={{ order: { xs: 1, md: 0 } }}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'rgba(4, 10, 8, 0.72)',
                borderRadius: 3,
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}
            >
              {/* Token Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={logoWrapper(logo, token)}
                  alt={identitySymbol}
                  sx={{ width: 56, height: 56 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
                  >
                    {identitySymbol}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                    {name}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Price Section */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#9ca3af',
                    mb: 1.5,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}
                >
                  {identitySymbol} Price
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: 'white', fontWeight: 600 }}
                  >
                    ${formatPrice(currentPrice)}
                  </Typography>
                  {currentPrice && price1hour && (
                    <Chip
                      icon={
                        isPositive ? (
                          <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
                        )
                      }
                      label={`${priceChangePercent}%`}
                      size="medium"
                      sx={{
                        backgroundColor: isPositive
                          ? 'rgba(16, 185, 129, 0.15)'
                          : 'rgba(239, 83, 80, 0.15)',
                        color: isPositive ? '#10B981' : '#EF5350',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        '& .MuiChip-icon': {
                          color: 'inherit'
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Key Synthetic Token Information */}
              <Box
                sx={{
                  border: '0px solid #34D399',
                  borderRadius: 2,
                  p: 2.5,
                  backgroundColor: 'rgba(52, 211, 153, 0.03)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#34D399',
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Token Details
                </Typography>
                <Stack
                  spacing={0}
                  divider={
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                  }
                >
                  <DetailRow label="Symbol" value={identitySymbol} />
                  <DetailRow label="Name" value={name} />
                  <DetailRow label="Token Type" value="Synthetic Asset" />
                  <DetailRow
                    label="Underlying Asset"
                    value={resolveLabel(
                      token?.underlying_type_code,
                      UNDERLYING_ASSET_CODES
                    )}
                  />
                  <DetailRow
                    label="Status"
                    value={token?.status ? 'Active' : 'Inactive'}
                    color={token?.status ? '#34D399' : '#EF5350'}
                  />
                </Stack>
              </Box>

              {/* Pricing & Mechanism */}
              <Box
                sx={{
                  border: '0px solid #34D399',
                  borderRadius: 2,
                  p: 2.5,
                  backgroundColor: 'rgba(52, 211, 153, 0.03)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#34D399',
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Pricing & Mechanism
                </Typography>
                <Stack
                  spacing={0}
                  divider={
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                  }
                >
                  <DetailRow
                    label="Pricing Method"
                    value={resolveLabel(
                      token?.pricing_type_code,
                      PRICING_METHOD_CODES
                    )}
                  />
                  <DetailRow
                    label="Collateral Architecture"
                    value={resolveLabel(
                      token?.collateral_type_code,
                      COLLATERAL_ARCHITECTURE_CODES
                    )}
                  />
                  <DetailRow
                    label="Collateralization Level"
                    value={resolveLabel(
                      token?.collateralization_type_code,
                      COLLATERALIZATION_LEVEL_CODES
                    )}
                  />
                  <DetailRow
                    label="Treasury Composition"
                    value={resolveLabel(
                      token?.treasury_type_code,
                      TREASURY_COMPOSITION_CODES
                    )}
                  />
                </Stack>
              </Box>

              {/* Action Buttons */}
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  color="success"
                  href="https://pecuscan.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    borderWidth: 2,
                    fontWeight: 600,
                    '&:hover': {
                      borderWidth: 2
                    }
                  }}
                >
                  View on Pecuscan
                </Button>

                {link && (
                  <Button
                    variant="contained"
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      backgroundColor: '#34D399',
                      color: 'black',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#5fb096'
                      }
                    }}
                  >
                    Learn More
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>

          {/* Right Column - Chart and Description */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{ marginTop: { xs: 4, md: 4 }, order: { xs: 0, md: 1 } }}
          >
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'rgba(4, 10, 8, 0.72)',
                borderRadius: 3,
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}
            >
              {/* Chart Component */}
              <Box sx={{ minHeight: 400 }}>
                <ProChart symbol={identitySymbol} chartSymbol={symbol} />
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Description */}
              {description && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: '#34D399', mb: 2, fontWeight: 700 }}
                  >
                    About
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#f0f0f0', lineHeight: 1.8 }}
                  >
                    {description}
                  </Typography>
                </Box>
              )}

              <TreasuryBackingPublic tokenType="SYNTHETICS" tokenId={token?.id} />

              {/* Technical Architecture */}
              <Box
                sx={{
                  border: '0px solid #34D399',
                  borderRadius: 2,
                  p: 2.5,
                  backgroundColor: 'rgba(52, 211, 153, 0.03)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#34D399',
                    mb: 2.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Architecture Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Underlying Asset Type"
                      value={resolveLabel(
                        token?.underlying_type_code,
                        UNDERLYING_ASSET_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Pricing Methodology"
                      value={resolveLabel(
                        token?.pricing_type_code,
                        PRICING_METHOD_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Collateral Type"
                      value={resolveLabel(
                        token?.collateral_type_code,
                        COLLATERAL_ARCHITECTURE_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Collateralization"
                      value={resolveLabel(
                        token?.collateralization_type_code,
                        COLLATERALIZATION_LEVEL_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Treasury Strategy"
                      value={resolveLabel(
                        token?.treasury_type_code,
                        TREASURY_COMPOSITION_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* <DetailBlock
                      label="Last Updated"
                      value={
                        token?.updated_at
                          ? new Date(token.updated_at).toLocaleDateString(
                              'en-US',
                              {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }
                            )
                          : 'N/A'
                      }
                    /> */}
                    <DetailBlock
                      label="Treasury Key"
                      value={token?.treasury_key}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Transactions Table - Full Width */}
          <Grid item xs={12} mt={2} sx={{ order: { xs: 2, md: 2 } }}>
            <Transactions />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default SyntheticPage;
